import React from "react";
import { Box, Stack, Typography } from "@mui/material";

export type ActivityTimelineItemProps = {
  icon?: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  meta?: React.ReactNode;
};

export const ActivityTimelineItem = ({ icon, primary, secondary, meta }: ActivityTimelineItemProps) => {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
      <Box
        sx={{
          width: 36,
          minWidth: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        {icon ?? (
          <span className="material-icons-outlined" style={{ fontSize: 20 }}>
            circle
          </span>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" color="text.primary" sx={{ wordBreak: "break-word" }}>
          {primary}
        </Typography>
        {secondary ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {secondary}
          </Typography>
        ) : null}
        {meta ? (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>
            {meta}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
};

export type ActivityTimelineGroup = {
  /** Stable key for React list */
  id: string;
  heading: string;
  items: ActivityTimelineItemProps[];
};

export type ActivityTimelineProps = {
  groups: ActivityTimelineGroup[];
  emptyMessage?: string;
  maxHeight?: number | string;
};

export const ActivityTimeline = ({ groups, emptyMessage = "No activity yet.", maxHeight = 420 }: ActivityTimelineProps) => {
  if (!groups?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: "auto",
        pr: 1,
        mr: -1,
      }}
    >
      <Stack spacing={2}>
        {groups.map((group) => (
          <Box key={group.id}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 0.5 }}>
              {group.heading}
            </Typography>
            <Stack spacing={0} divider={<Box sx={{ borderBottom: 1, borderColor: "divider", ml: 6 }} />}>
              {group.items.map((item, idx) => (
                <ActivityTimelineItem key={idx} {...item} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
